/**
 * Profil du joueur pour le classement : un pseudo et une couleur, qui forment
 * son avatar (les initiales du pseudo dans un anneau de la couleur choisie).
 * La validation qui fait foi est côté serveur (backend/src/player-pseudo.ts).
 */

export interface ProfileColor {
  value: string;
  /** Nom lu par les lecteurs d'écran. */
  label: string;
}

/** Les 8 teintes de la charte, sans rouge (réservé au game over). */
export const PROFILE_COLORS: ProfileColor[] = [
  { value: '#c8a24a', label: 'Sable' },
  { value: '#e0a370', label: 'Abricot' },
  { value: '#d6a2b8', label: 'Rose poudré' },
  { value: '#b49ad6', label: 'Lilas' },
  { value: '#8f9fe0', label: 'Pervenche' },
  { value: '#7fb3c8', label: 'Bleu glacier' },
  { value: '#6fc2a6', label: 'Menthe' },
  { value: '#a7c17a', label: 'Sauge' },
];

export const DEFAULT_AVATAR_COLOR = '#c8a24a';
export const PSEUDO_MIN_LENGTH = 2;
export const PSEUDO_MAX_LENGTH = 20;

const PSEUDO_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N} '’.-]*$/u;
const SKIPPED_WORDS = /^(le|la|les|de|du|des|l|d)$/i;

export function cleanPseudo(raw: string): string {
  return raw.normalize('NFC').trim().replace(/\s+/g, ' ');
}

/** Même règle de forme que le serveur (sans la liste de mots interdits). */
export function isPseudoSubmittable(raw: string): boolean {
  const pseudo = cleanPseudo(raw);
  const length = [...pseudo].length;
  return length >= PSEUDO_MIN_LENGTH && length <= PSEUDO_MAX_LENGTH && PSEUDO_PATTERN.test(pseudo);
}

/**
 * La première lettre des deux premiers mots qui comptent (« Le fil de laine » → FL,
 * « Cerf-volant » → CV). Un seul mot donne ses deux premières lettres (« Sextant » → SE).
 */
export function pseudoInitials(pseudo: string): string {
  const words = cleanPseudo(pseudo).split(/[\s'’.-]+/).filter(Boolean);
  const meaningful = words.filter((word) => !SKIPPED_WORDS.test(word));
  const source = meaningful.length > 0 ? meaningful : words;
  const [first, second] = source;
  if (!first) return '?';
  const firstLetter = (word: string) => Array.from(word)[0] ?? '';
  const letters = second ? firstLetter(first) + firstLetter(second) : Array.from(first).slice(0, 2).join('');
  return letters.toLocaleUpperCase('fr');
}

export function profileColorLabel(value: string): string {
  return PROFILE_COLORS.find((color) => color.value === value.toLowerCase())?.label ?? value;
}
